import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { ICorrespondentResultViewModel } from 'viewModels';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import HistoryIcon from '@mui/icons-material/History';
import { CorrespondentCardInfo, RouteInfoContainer } from './styles';

interface MapContainerStyle {
  width: string;
  height: string;
}

export interface Position {
  lat: number;
  lng: number;
}

interface RouteInfo {
  distance: '';
  duration: '';
}

interface InteractiveMapProps {
  initialPosition: Position;
  correspondents: ICorrespondentResultViewModel[];
}

const containerStyle: MapContainerStyle = {
  width: '100%',
  height: '400px',
};

export const InteractiveMap: React.FC<InteractiveMapProps> = ({
  initialPosition,
  correspondents,
}: InteractiveMapProps) => {
  const [center, setCenter] = useState<Position>(initialPosition);
  const [routeInfo, setRouteInfo] = useState<RouteInfo>();
  const [selectedCorrespondent, setSelectedCorrespondent] =
    useState<ICorrespondentResultViewModel>();

  const mapRef = useRef<google.maps.Map>();
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer>();
  const markerRef = useRef<google.maps.Marker>();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '',
  });

  const handleUserMarkerClick = useCallback(
    (correspondent: ICorrespondentResultViewModel) => {
      if (!mapRef.current) return;
      const directionsService = new google.maps.DirectionsService();
      const origin = initialPosition;
      const destination = {
        lat: correspondent.address.latitude,
        lng: correspondent.address.longitude,
      };
      directionsService.route(
        {
          origin,
          destination,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (
            result &&
            status === google.maps.DirectionsStatus.OK &&
            directionsRendererRef.current
          ) {
            directionsRendererRef.current.setDirections(result);
            const route = result.routes[0];
            const leg = route.legs[0];
            const distance = leg?.distance?.text;
            const duration = leg?.duration?.text;

            setRouteInfo({ distance, duration } as RouteInfo);
          }
        },
      );
      setCenter({ lat: correspondent.address.latitude, lng: correspondent.address.longitude });
      setSelectedCorrespondent(correspondent);
    },
    [initialPosition],
  );

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      directionsRendererRef.current = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
      });
      directionsRendererRef.current.setMap(map);

      markerRef.current = new google.maps.Marker({
        position: initialPosition,
        map,
        icon: {
          url: 'https://i.imgur.com/zzHvmxD.png',
          scaledSize: new google.maps.Size(50, 50),
        },
        title: 'Local da Solicitação',
      });
      correspondents.forEach((correspondent) => {
        if (markerRef.current) {
          if (
            !Number.isFinite(correspondent.address.latitude) ||
            !Number.isFinite(correspondent.address.longitude)
          )
            return;

          const newMarker = new google.maps.Marker({
            position: {
              lat: correspondent.address.latitude,
              lng: correspondent.address.longitude,
            },
            map,
            icon: {
              url: 'https://i.imgur.com/PrhLBvx.png',
              scaledSize: new google.maps.Size(50, 50),
            },
            title: `${correspondent.firstName ?? ''} ${correspondent.lastName ?? ''}`,
          });
          newMarker.addListener('click', () => handleUserMarkerClick(correspondent));
        }
      });

      markerRef.current?.setMap(map);
      setCenter(initialPosition);
    },
    [correspondents, handleUserMarkerClick, initialPosition],
  );

  const onUnmount = useCallback(() => {
    mapRef.current = undefined;
  }, []);

  // useEffect(() => {
  //   if (
  //     initialPosition &&
  //     Number.isFinite(initialPosition.lat) &&
  //     Number.isFinite(initialPosition.lng)
  //   )

  // }, [initialPosition]);

  useEffect(() => {
    if (mapRef.current) onLoad(mapRef.current);
  }, [onLoad]);

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={15}
      onLoad={onLoad}
      id="MAP_ID"
      onUnmount={onUnmount}
      options={{
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      }}>
      {selectedCorrespondent && (
        <InfoWindow
          onCloseClick={() => {
            setSelectedCorrespondent(undefined);
            setRouteInfo(undefined);
          }}
          position={{
            lat: selectedCorrespondent.address?.latitude,
            lng: selectedCorrespondent.address?.longitude,
          }}>
          <CorrespondentCardInfo>
            <div className="correspondentName">
              {`${selectedCorrespondent.firstName ?? '-'} ${
                selectedCorrespondent?.lastName ?? ''
              } `}
            </div>
            <div>
              {selectedCorrespondent?.address?.city ?? '-'} -{' '}
              {selectedCorrespondent?.address?.street ?? '-'}, nº{' '}
              {selectedCorrespondent?.address?.number ?? '-'}
            </div>
          </CorrespondentCardInfo>
        </InfoWindow>
      )}
      {routeInfo && (
        <RouteInfoContainer>
          <p className="icon-text">
            <AltRouteIcon /> Distância: {routeInfo.distance}
          </p>
          <p className="icon-text">
            <HistoryIcon /> Duração: {routeInfo.duration}
          </p>
        </RouteInfoContainer>
      )}
    </GoogleMap>
  ) : (
    <div>Carregando...</div>
  );
};
